import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import Icon from "@mui/material/Icon";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

import { useAuth } from "context/AuthContext";
import { useCart } from "context/CartContext";
import { currencyForCompany } from "lib/companyProfile";
import useStorefrontCopy from "hooks/useStorefrontCopy";
import VbsShell from "../components/VbsShell";
import { formatMoney, vbs } from "../lib/theme";

function QtyStepper({ qty, onDec, onInc }) {
  return (
    <Stack
      direction="row"
      alignItems="center"
      sx={{
        border: `1px solid ${vbs.hairline}`,
        borderRadius: "10px",
        bgcolor: vbs.canvas,
        px: 0.25,
      }}
    >
      <ButtonBase
        onClick={onDec}
        aria-label="Decrease quantity"
        sx={{ width: 36, height: 36, color: vbs.ink, borderRadius: "8px" }}
      >
        <Icon sx={{ fontSize: 18 }}>remove</Icon>
      </ButtonBase>
      <Typography sx={{ width: 28, textAlign: "center", fontWeight: 800, color: vbs.ink, fontSize: 15 }}>
        {qty}
      </Typography>
      <ButtonBase
        onClick={onInc}
        aria-label="Increase quantity"
        sx={{ width: 36, height: 36, color: vbs.ink, borderRadius: "8px" }}
      >
        <Icon sx={{ fontSize: 18 }}>add</Icon>
      </ButtonBase>
    </Stack>
  );
}

QtyStepper.propTypes = {
  qty: PropTypes.number.isRequired,
  onDec: PropTypes.func.isRequired,
  onInc: PropTypes.func.isRequired,
};

export default function CartPage() {
  const navigate = useNavigate();
  const { token, storefrontTenant, activeCompany } = useAuth();
  const { lines, removeItem, setQty, subtotal, itemSubtotal, coreSubtotal } = useCart();
  const { t } = useStorefrontCopy();
  const currency = currencyForCompany(storefrontTenant || activeCompany || "vbsus");
  const hasItems = (lines || []).length > 0;

  if (!token) {
    return (
      <VbsShell title={t("cart.title", "Cart")} onBack={() => navigate("/m/catalog")}>
        <Box
          sx={{
            mt: 2,
            mx: { xs: 2, sm: 3 },
            maxWidth: vbs.tabletMax,
            width: "100%",
            alignSelf: "center",
            p: 2.5,
            borderRadius: "14px",
            bgcolor: vbs.surface,
            border: `1px solid ${vbs.hairline}`,
            textAlign: "center",
          }}
        >
          <Box
            sx={{
              width: 56,
              height: 56,
              mx: "auto",
              mb: 1.5,
              borderRadius: "14px",
              bgcolor: vbs.accentSoft,
              display: "grid",
              placeItems: "center",
            }}
          >
            <Icon sx={{ color: vbs.accent, fontSize: 28 }}>lock</Icon>
          </Box>
          <Typography sx={{ fontSize: 16, fontWeight: 750, color: vbs.ink, mb: 0.75 }}>
            {t("common.signIn", "Sign in")}
          </Typography>
          <Typography sx={{ fontSize: 14, color: vbs.muted, lineHeight: 1.5, mb: 2 }}>
            {t("cart.signInRequired", "Checkout will require signing in to a customer account.")}
          </Typography>
          <ButtonBase
            onClick={() => navigate("/m/authentication/sign-in?next=/m/cart")}
            sx={{
              width: "100%",
              bgcolor: vbs.accent,
              color: "#fff",
              py: 1.4,
              borderRadius: "10px",
              fontSize: 15,
              fontWeight: 800,
              boxShadow: "0 10px 24px rgba(255,92,0,0.28)",
              "&:hover": { bgcolor: vbs.accentHover },
            }}
          >
            {t("common.signIn", "Sign in")}
          </ButtonBase>
        </Box>
      </VbsShell>
    );
  }

  const footer = hasItems ? (
    <Box sx={{ maxWidth: vbs.tabletMax, mx: "auto", width: "100%" }}>
      <Stack spacing={1.1}>
        <Stack direction="row" justifyContent="space-between" alignItems="baseline">
          <Typography sx={{ fontSize: 13, fontWeight: 700, color: vbs.muted }}>
            {t("cart.total", "Cart total")}
          </Typography>
          <Typography sx={{ fontSize: 22, fontWeight: 800, color: vbs.ink, letterSpacing: -0.3 }}>
            {formatMoney(subtotal, currency)}
          </Typography>
        </Stack>
        <ButtonBase
          onClick={() => navigate("/m/checkout")}
          sx={{
            width: "100%",
            bgcolor: vbs.accent,
            color: "#fff",
            py: 1.45,
            px: 2,
            borderRadius: "10px",
            fontSize: 15,
            fontWeight: 800,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 1,
            boxShadow: "0 10px 24px rgba(255,92,0,0.28)",
            "&:hover": { bgcolor: vbs.accentHover },
          }}
        >
          <Icon sx={{ fontSize: 20 }}>shopping_bag</Icon>
          <span>{t("cart.checkout", "Checkout")}</span>
          <Icon sx={{ fontSize: 20 }}>arrow_forward</Icon>
        </ButtonBase>
      </Stack>
    </Box>
  ) : null;

  return (
    <VbsShell title={t("cart.title", "Cart")} onBack={() => navigate("/m/catalog")} footer={footer}>
      <Box
        sx={{
          px: { xs: 2, sm: 3 },
          maxWidth: vbs.tabletMax,
          mx: "auto",
          width: "100%",
        }}
      >
        {!hasItems ? (
          <Box
            sx={{
              mt: 2,
              p: 2.75,
              borderRadius: "14px",
              bgcolor: vbs.surface,
              border: `1px solid ${vbs.hairline}`,
              textAlign: "center",
            }}
          >
            <Box
              sx={{
                width: 56,
                height: 56,
                mx: "auto",
                mb: 1.5,
                borderRadius: "14px",
                bgcolor: vbs.accentSoft,
                display: "grid",
                placeItems: "center",
              }}
            >
              <Icon sx={{ color: vbs.accent, fontSize: 28 }}>shopping_cart</Icon>
            </Box>
            <Typography sx={{ fontSize: 17, fontWeight: 750, color: vbs.ink, mb: 0.75 }}>
              {t("cart.emptyTitle", "Your cart is empty")}
            </Typography>
            <Typography sx={{ fontSize: 14, color: vbs.muted, lineHeight: 1.5, mb: 2.25 }}>
              {t("cart.emptyText", "Add products from the catalog to begin an order.")}
            </Typography>
            <ButtonBase
              onClick={() => navigate("/m/catalog")}
              sx={{
                width: "100%",
                bgcolor: vbs.accent,
                color: "#fff",
                py: 1.4,
                borderRadius: "10px",
                fontSize: 15,
                fontWeight: 800,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 0.75,
                boxShadow: "0 10px 24px rgba(255,92,0,0.28)",
                "&:hover": { bgcolor: vbs.accentHover },
              }}
            >
              <Icon sx={{ fontSize: 20 }}>storefront</Icon>
              <span>{t("common.browseCatalog", "Browse catalog")}</span>
            </ButtonBase>
          </Box>
        ) : (
          <Stack spacing={1.35} sx={{ mt: 1.25 }}>
            {(lines || []).map((line) => {
              const qty = Number(line.qty || 1) || 1;
              const unit = Number(line.unitPrice || 0) || 0;
              const core = Number(line.coreCharge || 0) || 0;
              const lineTotal = qty * (unit + core);

              return (
                <Box
                  key={line.documentId}
                  sx={{
                    p: 1.5,
                    borderRadius: "14px",
                    bgcolor: vbs.surface,
                    border: `1px solid ${vbs.hairline}`,
                    boxShadow: "0 8px 22px rgba(15,15,18,0.04)",
                  }}
                >
                  <Stack direction="row" spacing={1.35} alignItems="flex-start">
                    {line.imageUrl ? (
                      <Box
                        component="img"
                        src={line.imageUrl}
                        alt={line.name || line.sku || "Item"}
                        loading="lazy"
                        decoding="async"
                        sx={{
                          width: 84,
                          height: 84,
                          objectFit: "cover",
                          borderRadius: "12px",
                          bgcolor: vbs.canvas,
                          flexShrink: 0,
                          display: "block",
                        }}
                      />
                    ) : (
                      <Box
                        sx={{
                          width: 84,
                          height: 84,
                          borderRadius: "12px",
                          bgcolor: vbs.canvas,
                          display: "grid",
                          placeItems: "center",
                          flexShrink: 0,
                          border: `1px solid ${vbs.hairline}`,
                        }}
                      >
                        <Icon sx={{ color: vbs.subtle, fontSize: 28 }}>inventory_2</Icon>
                      </Box>
                    )}

                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={0.75}>
                        <Box sx={{ minWidth: 0 }}>
                          {line.sku ? (
                            <Typography
                              sx={{
                                fontFamily: vbs.fontMono,
                                fontSize: 10.5,
                                fontWeight: 750,
                                color: vbs.accent,
                                letterSpacing: 0.7,
                                textTransform: "uppercase",
                              }}
                            >
                              {line.sku}
                            </Typography>
                          ) : null}
                          <Typography
                            sx={{
                              mt: 0.2,
                              fontSize: 15.5,
                              fontWeight: 750,
                              lineHeight: 1.28,
                              color: vbs.ink,
                            }}
                          >
                            {line.name || line.sku || "Item"}
                          </Typography>
                          <Typography sx={{ mt: 0.45, fontSize: 14.5, fontWeight: 750, color: vbs.ink }}>
                            {formatMoney(unit, currency)}
                            {core > 0 ? (
                              <Typography
                                component="span"
                                sx={{ ml: 0.5, fontSize: 13, fontWeight: 600, color: vbs.muted }}
                              >
                                + {formatMoney(core, currency)} core
                              </Typography>
                            ) : null}
                          </Typography>
                        </Box>
                        <ButtonBase
                          onClick={() => removeItem(line.documentId)}
                          aria-label="Remove"
                          sx={{
                            width: 36,
                            height: 36,
                            borderRadius: "10px",
                            color: "#A51812",
                            bgcolor: "rgba(215,38,30,0.07)",
                            flexShrink: 0,
                            "&:active": { opacity: 0.7 },
                          }}
                        >
                          <Icon sx={{ fontSize: 18 }}>delete_outline</Icon>
                        </ButtonBase>
                      </Stack>

                      <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        sx={{ mt: 1.25 }}
                      >
                        <QtyStepper
                          qty={qty}
                          onDec={() => setQty(line.documentId, Math.max(1, qty - 1))}
                          onInc={() => setQty(line.documentId, Math.min(99, qty + 1))}
                        />
                        <Typography sx={{ fontSize: 16, fontWeight: 800, color: vbs.ink, letterSpacing: -0.2 }}>
                          {formatMoney(lineTotal, currency)}
                        </Typography>
                      </Stack>
                    </Box>
                  </Stack>
                </Box>
              );
            })}

            <Box
              sx={{
                p: 2,
                borderRadius: "14px",
                bgcolor: vbs.surface,
                border: `1px solid ${vbs.hairline}`,
              }}
            >
              <Typography
                sx={{
                  fontSize: 11.5,
                  fontWeight: 800,
                  letterSpacing: 1,
                  textTransform: "uppercase",
                  color: vbs.subtle,
                  mb: 1.25,
                }}
              >
                {t("cart.summary", "Summary")}
              </Typography>
              <Stack spacing={0.9}>
                <Stack direction="row" justifyContent="space-between">
                  <Typography sx={{ color: vbs.muted, fontSize: 14.5 }}>Subtotal</Typography>
                  <Typography sx={{ fontWeight: 750, fontSize: 14.5 }}>
                    {formatMoney(itemSubtotal, currency)}
                  </Typography>
                </Stack>
                {coreSubtotal > 0 ? (
                  <Stack direction="row" justifyContent="space-between">
                    <Typography sx={{ color: vbs.muted, fontSize: 14.5 }}>Core</Typography>
                    <Typography sx={{ fontWeight: 750, fontSize: 14.5 }}>
                      {formatMoney(coreSubtotal, currency)}
                    </Typography>
                  </Stack>
                ) : null}
                <Box sx={{ height: 1, bgcolor: vbs.hairline, my: 0.35 }} />
                <Stack direction="row" justifyContent="space-between" alignItems="baseline">
                  <Typography sx={{ fontWeight: 750, fontSize: 15 }}>{t("cart.total", "Cart total")}</Typography>
                  <Typography sx={{ fontWeight: 800, fontSize: 18, color: vbs.accent, letterSpacing: -0.25 }}>
                    {formatMoney(subtotal, currency)}
                  </Typography>
                </Stack>
              </Stack>
              {coreSubtotal > 0 ? (
                <Typography sx={{ mt: 1.25, fontSize: 12.5, color: vbs.muted, lineHeight: 1.45 }}>
                  {t("cart.refundableDeposit", "Deposit is refundable after return and approval.")}
                </Typography>
              ) : null}
            </Box>
          </Stack>
        )}
      </Box>
    </VbsShell>
  );
}
